import React from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatDate } from '../../utils/helpers';

/**
 * ExperimentTimelineSection Component
 *
 * Displays experiment timeline information: created, updated, and started dates.
 *
 * @param {Object} props
 * @param {Object} props.experiment - Experiment object
 */
function ExperimentTimelineSection({ experiment }) {
  const theme = useTheme();

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Divider
          sx={{
            my: 0.75,
            borderColor: theme.palette.divider,
            borderWidth: '1px',
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontSize: '0.9375rem',
            fontWeight: 600,
            color: theme.palette.text.primary,
            mb: 0.75,
            letterSpacing: '-0.01em',
          }}
        >
          Timeline
        </Typography>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Card
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '10px',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <CardContent sx={{ p: 1.5 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(3, 1fr)',
                },
                gap: 1.5,
              }}
            >
              {/* Created */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    mb: 0.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Created
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.875rem',
                    color: theme.palette.text.primary,
                  }}
                >
                  {formatDate(experiment.created_at)}
                </Typography>
              </Box>

              {/* Updated */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    mb: 0.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Updated
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.875rem',
                    color: theme.palette.text.primary,
                  }}
                >
                  {formatDate(experiment.updated_at)}
                </Typography>
              </Box>

              {/* Started */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    mb: 0.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Started
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.875rem',
                    color: theme.palette.text.primary,
                  }}
                >
                  {experiment.start_date
                    ? formatDate(experiment.start_date)
                    : 'Not started'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </>
  );
}

export default ExperimentTimelineSection;
